import axios from 'axios';
import React, { createContext, useEffect, useReducer, useRef } from 'react';

export const UserContext = createContext();


const initialState = {
	user: null,
	status: 'idle',
	error: null,
	restorePassword: { success: null, error: null },
	loginIntent: null,
	loginSuccess: { user: null, owner: null },
};

function reducer(state, action) {
	switch (action.type) {
		case 'SET_USER':
			return { ...state, user: action.payload, status: 'authenticated', error: null };
		case 'LOGIN_FAIL':
			return { ...state, user: { valid: false, error: action.payload }, status: 'error' };
		case 'CLEAR_USER':
			// return { ...state, user: null, status: 'idle' };
			return { ...initialState };
		case 'SET_LOGIN_INTENT':
			if (
				state.loginIntent &&
				state.loginIntent.mode === 'implicit' &&
				action.payload.mode === 'implicit'
			) {
				return state; // blocca il secondo login implicito
			}
			return { ...state, loginIntent: action.payload };
		case 'CLEAR_LOGIN_INTENT':
			return { ...state, loginIntent: null };
		case 'LOGIN_SUCCESS_FLAG':
			return {
				...state,
				loginSuccess: {
					...state.loginSuccess,
					[action.payload.type]: action.payload.success,
				},
			};
		case 'RESTORE_PASSWORD_SUCCESS':
			return { ...state, restorePassword: { success: true, error: null } };
		case 'RESTORE_PASSWORD_FAIL':
			return { ...state, restorePassword: { success: false, error: action.payload } };
		case 'RESET_RESTORE_PASSWORD':
			return { ...state, restorePassword: { success: null, error: null } };
		default:
			return state;
	}
}

const useUser = () => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const storedUserType = useRef(localStorage.getItem('userType'));
	const hasDispatchedInitialImplicitLogin = useRef(false);

	// === LOGIN GENERICO ===
	const login = async (data, type = 'user', isAnonymus = false) => {
		try {
			const response = await axios.post(`/api/${type}/login`, {
				email: data.email,
				password: data.password,
				isAnonymus,
			});
			if (response.data.success) {
				dispatch({ type: 'SET_USER', payload: { ...response.data.user, valid: true } });
				localStorage.setItem('userType', type);
				return response.data.user;
			} else {
				throw new Error(response.data.error);
			}
		} catch (err) {
			dispatch({ type: 'LOGIN_FAIL', payload: err.message });
			throw err;
		}
	};

	const verifyEmail = async (token) => {
		try {
			const res = await axios.get(`/api/user/verify-email/${token}`);
			if (res.data.success) {
				dispatch({ type: 'SET_USER', payload: { ...res.data.user, valid: true } });
				localStorage.setItem('userType', 'user');
				return { success: true };
			} else {
				return { success: false, error: res.data.error };
			}
		} catch (err) {
			return { success: false, error: err.message };
		}
	};

	const ownerImplicitLogin = () => {
		dispatch({ type: 'SET_LOGIN_INTENT', payload: { type: 'owner', mode: 'implicit' } });
	};

	const userImplicitLogin = () => {
		dispatch({ type: 'SET_LOGIN_INTENT', payload: { type: 'user', mode: 'implicit' } });
	};

	const userExplicitLogin = (data, isAnonymus = false) => {
		dispatch({
			type: 'SET_LOGIN_INTENT',
			payload: { type: 'user', mode: 'explicit', data: { ...data, isAnonymus } },
		});
	};

	const userSignup = (data) => {
		dispatch({
			type: 'SET_LOGIN_INTENT',
			payload: { type: 'user', mode: 'signup', data },
		});
	};

	const userGlogin = async (response) => {
		try {
			if (response.error) {
				throw new Error('Errore nella risposta di Google');
			}
			const { data } = await axios.post('/api/user/GLogin', { token: response.credential });
			if (data.success) {
				dispatch({ type: 'SET_USER', payload: { ...data.user, valid: true } });
				localStorage.setItem('userType', 'user');
			} else {
				dispatch({ type: 'LOGIN_FAIL', payload: data.error });
			}
		} catch (error) {
			console.error('Login Google fallito:', error);
			dispatch({ type: 'LOGIN_FAIL', payload: 'Login Google fallito' });
		}
	};

	const eraseUser = (noLogout = false) => {
		dispatch({ type: 'CLEAR_USER' });
		localStorage.removeItem('userType');
		localStorage.removeItem('noAnimation');
		storedUserType.current = null;

		// blocca temporaneamente l'implicit login
		window.__disableImplicitLogin = true;

		if (!noLogout) axios.post('/api/user/logout');
	};

	const restorePasswordRequest = (mail, userType) => {
		dispatch({ type: 'RESET_RESTORE_PASSWORD' });
		axios.post(`/api/${userType}/password/restore/request`, { mail }).then(({ data }) => {
			if (data.success) {
				dispatch({ type: 'RESTORE_PASSWORD_SUCCESS' });
			} else {
				dispatch({ type: 'RESTORE_PASSWORD_FAIL', payload: data.error });
			}
		});
	};

	const resetPassword = (id, userId, password, userType) => {
		dispatch({ type: 'RESET_RESTORE_PASSWORD' });
		axios
			.post(`/api/${userType}/password/restore/set`, { id, userId, password })
			.then(({ data }) => {
				if (data.success) {
					dispatch({ type: 'RESTORE_PASSWORD_SUCCESS' });
				} else {
					dispatch({ type: 'RESTORE_PASSWORD_FAIL', payload: data.error });
				}
			});
	};

	const updateUser = (userData) => {
		dispatch({ type: 'SET_USER', payload: { ...state.user, ...userData } });
	};

	// === EFFECT: LOGIN LOGICA CENTRALE ===
	useEffect(() => {
		if (!state.loginIntent) return;
		let active = true;
		let alreadyHandled = false;

		const executeLogin = async () => {
			const loginType = state.loginIntent?.type;

			try {
				if (alreadyHandled) return;
				alreadyHandled = true;

				// --- LOGIN IMPLICITO ---
				if (state.loginIntent.mode === 'implicit') {
					const res = await axios.post(`/api/${loginType}/authenticate`);
					if (active) {
						if (res.data.success) {
							dispatch({
								type: 'SET_USER',
								payload: { ...res.data.user, valid: true },
							});
							dispatch({
								type: 'LOGIN_SUCCESS_FLAG',
								payload: { type: loginType, success: true },
							});
							localStorage.setItem('userType', loginType);
						} else {
							dispatch({ type: 'LOGIN_FAIL', payload: 'Implicit login failed' });
							dispatch({
								type: 'LOGIN_SUCCESS_FLAG',
								payload: { type: loginType, success: false },
							});
							localStorage.removeItem('userType');
						}
					}
				}

				// --- LOGIN ESPLICITO ---
				else if (state.loginIntent.mode === 'explicit') {
					await login(
						state.loginIntent.data,
						loginType,
						state.loginIntent.data?.isAnonymus
					);
				}

				// --- SIGNUP UTENTE ---
				else if (state.loginIntent.mode === 'signup') {
					const res = await axios.post('/api/user/signup', state.loginIntent.data);

					if (res.data.success) {
						sessionStorage.removeItem('pendingOrderClaim');
						const emailVerified = res.data.user?.emailVerified ?? false;

						const userPayload = {
							...res.data.user,
							valid: emailVerified,
							pendingVerification: !emailVerified,
						};

						dispatch({ type: 'SET_USER', payload: userPayload });

						if (!emailVerified) {
							// opzionale: rimuovo il tipo utente per evitare login automatico
							localStorage.removeItem('userType');
						} else {
							localStorage.setItem('userType', 'user');
						}
					} else {
						dispatch({ type: 'LOGIN_FAIL', payload: res.data.error });
					}
				}
			} catch (err) {
				dispatch({ type: 'LOGIN_FAIL', payload: err.message });
				localStorage.removeItem('userType');
				window.location.href = '/';
			} finally {
				dispatch({ type: 'CLEAR_LOGIN_INTENT' });
			}
		};

		executeLogin();

		return () => {
			active = false;
		};
	}, [state.loginIntent]);

	// === EFFECT: reset dati di restore all'avvio ===
	useEffect(() => {
		dispatch({ type: 'RESET_RESTORE_PASSWORD' });
	}, []);

	// EFFECT: al montaggio, leggo userType da localStorage e faccio login implicito solo se non c'è user o loginIntent
	useEffect(() => {
		if (
			window.__disableImplicitLogin ||      // blocca se eraseUser ha appena pulito lo stato
			!storedUserType.current ||
			state.loginIntent ||
			state.user ||
			hasDispatchedInitialImplicitLogin.current
		) return;

		hasDispatchedInitialImplicitLogin.current = true;
		dispatch({
			type: 'SET_LOGIN_INTENT',
			payload: { type: storedUserType.current, mode: 'implicit' },
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	return {
		loginOwnerAsync: (data) => login(data, 'owner'),
		loginUserAsync: (data, isAnonymus = false) => login(data, 'user', isAnonymus),
		ownerImplicitLogin,
		userImplicitLogin,
		userExplicitLogin,
		userSignup,
		userGlogin,
		eraseUser,
		restorePasswordRequest,
		resetPassword,
		updateUser,
		setUser: (u) => dispatch({ type: 'SET_USER', payload: u }),
		user: state.user,
		ownerLoginSuccess: state.loginSuccess.owner,
		userLoginSuccess: state.loginSuccess.user,
		restorePasswordData: state.restorePassword,
		state: state,
		clearStatePassword: (u) => dispatch({ type: 'RESET_RESTORE_PASSWORD', payload: u }),
		verifyEmail
	};
};

export const UserProvider = ({ children }) => {
	const user = useUser();
	return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
